import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { useOnboardingStore } from "../../../store/onboardingStore";

interface SearchPreviousProductsProps {
  previousProducts: any[];
  isPreviousProductsLoading: boolean;
  handlePreviousProductSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function SearchPreviousProducts({
  previousProducts,
  isPreviousProductsLoading,
  handlePreviousProductSelect,
}: SearchPreviousProductsProps) {
  const { language } = useAuth();
  const fetchPreviousProducts = useOnboardingStore((state) => state.fetchPreviousProducts);
  const [inputValue, setInputValue] = useState("");
  const safeProducts = Array.isArray(previousProducts) ? previousProducts : [];

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      fetchPreviousProducts(inputValue);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [fetchPreviousProducts, inputValue]);
  
  return (
    <div className="flex-1 relative">
      <Autocomplete
        id="previous-products-autocomplete"
        options={safeProducts}
        loading={isPreviousProductsLoading}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        inputValue={inputValue}
        filterOptions={(options) => options}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear") {
            setInputValue(value);
          }
        }}
        onChange={(_, product) => {
          if (product) handlePreviousProductSelect({ target: { value: product.id } } as any);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={dictionary["search-previous-products-placeholder"][language]}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Search className="w-5 h-5 text-gray-500 mr-2" />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isPreviousProductsLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              className: "border border-gray-100 rounded-md bg-white",
              style: {
                color: 'black',
                WebkitTextFillColor: 'black', // For webkit browsers
              },
            }}
          />
        )}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement> & { 'data-option-index'?: number }, option) => (
          <li {...props} key={option.id || `product-${props['data-option-index']}`} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
            <div className="flex items-center">
              {option.images && option.images[0] && (
                <img
                  src={option.images[0]}
                  alt={option.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                />
              )}
              <div>
                <div className="font-medium">{option.name || 'Unnamed Product'}</div>
                <div className="text-sm text-gray-500">${option.price || '0.00'}</div>
              </div>
            </div>
          </li>
        )}
        noOptionsText="No products found"
        loadingText="Loading products..."
        className="w-full"
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '48px',
            borderRadius: '0.375rem',
            padding: '0 14px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--fluxi-primary-hex, #4368E5)',
              borderWidth: '1px',
            },
          },
          '& .MuiAutocomplete-endAdornment': {
            right: '8px',
          },
          '& .MuiAutocomplete-inputRoot': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAutocomplete-input': {
            height: '40px',
            padding: '0 !important',
            color: 'black',
          }
        }}
      />
    </div>
  );
}

export default SearchPreviousProducts;
